import {
  getAccessToken,
  getRefreshToken,
  getUserInfo,
} from "@/lib/storeManager";

function getSdBaseUrl(): string {
  const fromEnv = (
    import.meta.env.VITE_SD_APP_URL as string | undefined
  )?.trim();
  if (!fromEnv) {
    throw new Error("VITE_SD_APP_URL is not set in the environment.");
  }
  return fromEnv.replace(/\/$/, "");
}

function encodeUserPayload(userInfo: unknown): string | null {
  if (!userInfo || typeof userInfo !== "object") {return null;}
  try {
    const json = JSON.stringify(userInfo);
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return null;
  }
}

/**
 * Builds Service Desk SSO handoff URL with tokens in the hash fragment
 * (hash is not sent to the SD server on the first request).
 */
export function buildSdSsoHandoffUrl(): string | null {
  const access = getAccessToken();
  const refresh = getRefreshToken();
  if (!access || !refresh) {return null;}

  const params = new URLSearchParams();
  params.set("access", access);
  params.set("refresh", refresh);

  const userInfo = getUserInfo();
  if (userInfo?.id) {
    params.set("uid", String(userInfo.id));
  }

  const userEncoded = encodeUserPayload(userInfo);
  if (userEncoded) {
    params.set("user", userEncoded);
  }

  return `${getSdBaseUrl()}/sd/sso-handoff#${params.toString()}`;
}

/**
 * Move the user from PTS into the Service Desk app (same tab by default).
 * Passes the current PTS session so SD can bootstrap without re-login.
 */
export function openServiceDeskApp(options?: { sameTab?: boolean }): void {
  const sameTab = options?.sameTab !== false;
  const handoffUrl = buildSdSsoHandoffUrl();
  const fallbackUrl = `${getSdBaseUrl()}/sd/login`;
  const target = handoffUrl ?? fallbackUrl;

  if (sameTab) {  
    window.location.assign(target);
    return;
  }

  window.open(target, "_blank", "noopener,noreferrer");
}
